import { Text } from "@gs-ux-uitoolkit-react/text";
import { FormControl } from "@gs-ux-uitoolkit-react/form";
import { Input, LegacyInputIcon, InputProps } from "@gs-ux-uitoolkit-react/input";
import { DatePickerOptions } from "@gs-ux-uitoolkit-react/datepicker";
import { ChangeEvent, memo, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { dayjs } from "../../pages/advisor-invitations/Onboarding/utils";

type Props = {
  label?: string;
  name: string;
  placeholder?: string;
  type?: InputProps["type"];
  size?: InputProps["size"];
  leadingContent?: InputProps["leadingContent"];
  disabled?: boolean;
  helperText?: string;
  autoFocus?: boolean;
  dateFormat?: string;
  maxDate?: DatePickerOptions["maxDate"];
  minDate?: DatePickerOptions["minDate"];
  disableDates?: DatePickerOptions["disable"];
  locale?: DatePickerOptions["locale"];
  error?: string;
  className?: string;
  value?: string;
  isRequired?: boolean;
  maxLength?: number;
  readOnly?: boolean;
  setValue: (newVal?: string) => void;
  validateField?: (val?: string)=>void;
  touched?: boolean;
  onBlur?: () => void;
};

export const FormDateInput = memo(function ({
  label,
  helperText,
  disabled,
  className,
  isRequired,
  error,
  value,
  setValue,
  maxLength,
  readOnly,
  validateField,
  name,
  size,
  onBlur
}: Props) {
  const [inputValue, setInputValue] = useState<string>(value || "");
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (value && value?.length)
      setInputValue(dayjs(value).format("MM/DD/YYYY"));
    else setInputValue("")
  }, [value]);

  const handleInputFocus = () => ref.current?.select();

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const value1 = evt.target.value;
    if (/(.*)[a-zA-Z](.*)/.test(value1))
      return undefined;
    const endsWithSlash = value1.endsWith("/");

    /*
    determine whether we should add a "/" or not
     */
    const parts = value1.split("/");

    if (inputValue.length > value1.length) {
      setInputValue(value1);
    } else if (endsWithSlash) {
      setInputValue(value1);
    } else if (parts.length === 1 && !endsWithSlash) {
      // test to see if we need to append '/' after month
      const month = parseFloat(parts[0]);
      if ((month === 0 || month === 1) && parts[0] !== '01') {
        setInputValue(value1);
      } else if (month > 12) {
        setInputValue(`${value1[0]}/${value1[1]}`);
      } else {
        setInputValue(`${value1}/`);
      }
    } else if (parts.length === 2) {
      // test if we need to append a second '/'
      const day = parseFloat(parts[1]);
      const datesOneTwoThree = ['01', '02', '03'];
      if (day < 4 && !datesOneTwoThree.includes(parts[1])) {
        setInputValue(value1);
      } else {
        setInputValue(`${value1}/`);
      }
    } else if (parts.length === 3) {
      // don't append '/' if there are already 3 parts
      setInputValue(value1);
    } else {
      setInputValue(value1);
    }
  };

  const handleInputBlur = () => {
    /*
    try to format inputValue into a ISO date, if unsuccessful set the error
     */
    const dt = dayjs(inputValue);
    if (dt.isValid()) {
      handleSubmit(dt.toDate());
    } else {
      setValue(undefined);
    }
    if (onBlur) {
      onBlur();
    }
  };

  const handleSubmit = (newValue: Date) => {
    // for official submission we must use ISO date, for display we use more casual and familiar US date format
    const formattedVal = dayjs(newValue).format("YYYY-MM-DD")
    setValue(formattedVal);
    if(validateField)validateField(formattedVal)
  };

  return (
    <FormControl
      className={clsx({
        [`cursor-not-allowed pointer-events-none opacity-50 ${className || ""}`]: disabled,
        [`${className ? className : ""} relative`]: !disabled
      })}
      status={error ? "error" : "none"}
    >
      {label ? (
        <Text typography="textBody02Medium" className="mb-1" color="colorTextNeutralBold">
          <label htmlFor={name}>
            {label}
          </label>
          {isRequired && (
            <span className="text-gray-90 ml-1">*</span>
          )}
        </Text>
      ) : null}
      {readOnly && (
        <Input
          size={size}
          type="text"
          placeholder="MM/DD/YYYY"
          value={value}
          maxLength={maxLength}
          name={name}
          inputId={name}
          readOnly={readOnly}
          leadingContent={<LegacyInputIcon name="calendar-today" type="filled" color="gray060" />}
        />
      )}
      {!readOnly && (
        <Input
          data-input
          type="text"
          inputId={name}
          placeholder="MM/DD/YYYY"
          inputRef={instance => { ref.current = instance; }}
          value={inputValue}
          maxLength={maxLength}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          name={name}
          onFocus={handleInputFocus}
          leadingContent={<LegacyInputIcon name="calendar-today" type="filled" color="gray060" />}
        />
      )}
      {helperText && (
        <p className="text-xs text-gray-60 mt-1 font-normal">{helperText}</p>
      )}
      {(error) ? (
        <Text typography="textCaption01Regular" color="red060" className="mt-1 absolute inset-auto">
          {error}
        </Text>
      ): null}
    </FormControl>
  );
} ,(prevProps, nextProps) => {
  return prevProps.value === nextProps.value &&
    prevProps.error === nextProps.error &&
    prevProps.name === nextProps.name &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.isRequired === nextProps.isRequired &&
    prevProps.validateField === nextProps.validateField &&
    prevProps.setValue === nextProps.setValue &&
    prevProps.label === nextProps.label
} )
